import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Container,
  Fade,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import {
  CheckCircleOutline as CheckIcon,
  InfoOutlined as InfoIcon,
  Calculate as CalculateIcon,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  REGEX_PATTERNS,
  MODEL_OPTIONS,
  MODEL_LEGENDS,
  MODEL_HELPER_TEXT,
  ERROR_MESSAGES,
} from "../../Config/constants";
import ResultsTable from "./ResultsTable";
import IcfesDialog from "./IcfesDialog";
import Footer from "../Footer";

const Simulator = () => {
  const [modelo, setModelo] = useState(0);
  const [leyenda, setLeyenda] = useState("");
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showResult, setShowResult] = useState(false);

  // Get current model option for helper text
  const currentModelOption = useMemo(() => {
    return MODEL_OPTIONS.find((opt) => opt.value === modelo);
  }, [modelo]);

  const helperText = useMemo(() => {
    if (error) return "";
    return MODEL_HELPER_TEXT[modelo] || MODEL_HELPER_TEXT[0];
  }, [modelo, error]);

  useEffect(() => {
    const newLeyenda = MODEL_LEGENDS[modelo] || "";
    setLeyenda(newLeyenda);

    // Show dialog when model 3 is selected
    if (modelo === 3) {
      setDialogOpen(true);
    }
  }, [modelo]);

  // Animate result appearance
  useEffect(() => {
    if (resultado) {
      setShowResult(false);
      const timer = setTimeout(() => setShowResult(true), 100);
      return () => clearTimeout(timer);
    }
  }, [resultado]);

  const handleModelChange = useCallback((event) => {
    setDialogOpen(false);
    setModelo(event.target.value);
    setError("");
    setResultado("");
    setInputValue("");
    setShowResult(false);
  }, []);

  const handleInputChange = useCallback((event) => {
    setInputValue(event.target.value);
    setError("");
    setResultado("");
    setShowResult(false);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const calculateResult = useCallback(() => {
    const value = inputValue.trim();

    if (!value) {
      setError("Por favor ingrese un valor");
      return;
    }

    switch (modelo) {
      case 1: {
        if (!REGEX_PATTERNS.RANGE_100_TO_400.test(value)) {
          setError(ERROR_MESSAGES.INVALID_RANGE_100_400);
          return;
        }
        setResultado(((parseFloat(value) - 100) / 3).toFixed(2));
        break;
      }
      case 2: {
        const values = value.split(",");
        let total = 0;
        let count = 0;
        let hasError = false;

        values.forEach((element) => {
          const trimmed = element.trim();
          if (!REGEX_PATTERNS.DECIMAL_NUMBER.test(trimmed)) {
            hasError = true;
            return;
          }
          total += parseFloat(trimmed);
          count++;
        });

        if (hasError || count === 0) {
          setError(ERROR_MESSAGES.INVALID_COMMA_SEPARATED);
          return;
        }

        setResultado((total / count).toFixed(2));
        break;
      }
      case 3:
      case 4: {
        if (!REGEX_PATTERNS.RANGE_0_TO_500.test(value)) {
          setError(ERROR_MESSAGES.INVALID_RANGE_0_500);
          return;
        }
        setResultado((parseFloat(value) / 5).toFixed(2));
        break;
      }
      default:
        setError("Por favor seleccione un periodo de realización de prueba");
    }
  }, [modelo, inputValue]);

  const isInputDisabled = modelo === 0;

  return (
    <>
      <main>
        <Box
          sx={{
            bgcolor: "background.paper",
            pt: { xs: 4, md: 8 },
            pb: { xs: 4, md: 6 },
          }}
        >
          <Container maxWidth="md">
            {/* Header Section */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                component="h1"
                variant="h3"
                align="center"
                color="text.primary"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
                }}
              >
                Simulador de conversión de pruebas ICFES
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: "auto" }}
              >
                Convierte tu puntaje ICFES a la escala de 1 a 100 utilizada por
                la Universidad de Cartagena
              </Typography>
            </Box>

            {/* Form Card */}
            <Card
              elevation={3}
              sx={{
                p: { xs: 2, md: 4 },
                bgcolor: "white",
                transition: "box-shadow 0.3s ease",
                "&:hover": {
                  boxShadow: 6,
                },
              }}
            >
              <CardContent>
                <FormControl fullWidth>
                  {/* Period Selection */}
                  <InputLabel id="select-modelo-label">
                    Periodo de realización de prueba
                  </InputLabel>
                  <Select
                    labelId="select-modelo-label"
                    id="select-modelo"
                    value={modelo}
                    onChange={handleModelChange}
                    label="Periodo de realización de prueba"
                    aria-describedby="modelo-helper-text"
                    renderValue={(selected) => {
                      const option = MODEL_OPTIONS.find(
                        (opt) => opt.value === selected
                      );
                      return option?.label || "Seleccione un periodo";
                    }}
                  >
                    {MODEL_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <ListItemText
                          primary={option.label}
                          secondary={option.description}
                          primaryTypographyProps={{ fontWeight: 500 }}
                          secondaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                          }}
                        />
                        <Tooltip title={option.description} placement="right">
                          <InfoIcon
                            fontSize="small"
                            sx={{ ml: 1, color: "action.active" }}
                          />
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                  {currentModelOption && (
                    <FormHelperText id="modelo-helper-text">
                      {currentModelOption.description}
                    </FormHelperText>
                  )}

                  {/* Input Field */}
                  <TextField
                    id="input-values"
                    label={leyenda || "Seleccione primero un periodo"}
                    variant="outlined"
                    fullWidth
                    value={inputValue}
                    onChange={handleInputChange}
                    disabled={isInputDisabled}
                    error={Boolean(error)}
                    helperText={error || helperText}
                    sx={{ mt: 3 }}
                    aria-describedby="input-helper-text"
                    InputProps={{
                      sx: {
                        bgcolor: isInputDisabled ? "grey.100" : "white",
                      },
                    }}
                  />

                  {/* Error Alert */}
                  <Collapse in={Boolean(error)}>
                    <Alert
                      variant="filled"
                      severity="error"
                      sx={{ mt: 2, borderRadius: 2 }}
                    >
                      {error}
                    </Alert>
                  </Collapse>

                  {/* Submit Button */}
                  <Stack sx={{ pt: 4 }} direction="row" justifyContent="center">
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={calculateResult}
                      disabled={isInputDisabled}
                      startIcon={<CalculateIcon />}
                      sx={{
                        py: 1.5,
                        fontSize: "1.1rem",
                        transition: "transform 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                        },
                        "&:active": {
                          transform: "translateY(0)",
                        },
                      }}
                    >
                      Simular resultado
                    </Button>
                  </Stack>
                </FormControl>
              </CardContent>
            </Card>

            {/* Result Card */}
            <Collapse in={Boolean(resultado)}>
              <Zoom in={showResult} timeout={300}>
                <Card
                  elevation={4}
                  sx={{
                    mt: 4,
                    p: 3,
                    bgcolor: "success.light",
                    border: "2px solid",
                    borderColor: "success.main",
                    textAlign: "center",
                  }}
                >
                  <CardContent>
                    <Fade in={showResult} timeout={500}>
                      <Box>
                        <CheckIcon
                          sx={{
                            fontSize: 48,
                            color: "success.main",
                            mb: 1,
                          }}
                        />
                        <Typography
                          variant="h6"
                          color="success.dark"
                          gutterBottom
                        >
                          Resultado de la simulación
                        </Typography>
                        <Typography
                          variant="h2"
                          color="success.dark"
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: "2.5rem", md: "3.5rem" },
                          }}
                        >
                          {resultado}
                          <Typography
                            component="span"
                            variant="h4"
                            color="success.main"
                          >
                            /100
                          </Typography>
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 2 }}
                        >
                          Este es tu puntaje convertido a la escala universitaria
                        </Typography>
                      </Box>
                    </Fade>
                  </CardContent>
                </Card>
              </Zoom>
            </Collapse>
          </Container>
        </Box>

        <ResultsTable />
        <Footer />
        <IcfesDialog open={dialogOpen} onClose={handleDialogClose} />
      </main>
    </>
  );
};

export default Simulator;
